import React from 'react';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { CalendarIcon, FlagIcon, AwardIcon, BriefcaseIcon, ArrowRightIcon, Volume2Icon, LayoutIcon, LightbulbIcon, MonitorIcon, VideoIcon, WrenchIcon, UsersIcon, ShieldCheckIcon, BuildingIcon, MessageSquareIcon, ClipboardListIcon, SettingsIcon, CheckCircleIcon, QuoteIcon, PlayIcon, MicIcon } from 'lucide-react';
export function HomePage() {
  useScrollReveal();
  return <main className="w-full overflow-hidden" data-id="element-267">
      {/* SECTION 1 - HERO */}
      <section className="relative min-h-screen flex items-center pt-20 pb-12 px-6" data-id="element-268">
        {/* Background Image Placeholder */}
        <div className="absolute inset-0 z-0" data-id="element-269">
          <div className="w-full h-full bg-brand-graphite relative" data-id="element-270">
            <div className="absolute inset-0 bg-gradient-to-b from-brand-black/80 via-brand-black/60 to-brand-black z-10" data-id="element-271" />
            <div className="absolute inset-0 flex items-center justify-center opacity-10" data-id="element-272">
              <MicIcon className="w-64 h-64 text-white" data-id="element-273" />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto w-full relative z-20 mt-12" data-id="element-274">
          <div className="max-w-4xl reveal" data-id="element-275">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-blue/10 border border-brand-blue/20 text-brand-blue text-sm font-medium mb-6" data-id="element-276">
              <span className="w-2 h-2 rounded-full bg-brand-blue animate-pulse" data-id="element-277" />
              Botswana's Premier Production Partner
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight" data-id="element-278">
              Professional Audio-Visual &amp;{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-cyan" data-id="element-279">
                Event Production
              </span>{' '}
              Solutions in Botswana
            </h1>
            <p className="text-lg md:text-xl text-brand-muted-light mb-10 leading-relaxed max-w-3xl" data-id="element-280">
              From sound systems and stage hire to lighting, visual equipment,
              and full event technical support — AUDIOTECH delivers dependable
              solutions for events, installations, and corporate environments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-16" data-id="element-281">
              <Link to="/contact" className="btn-glow text-lg px-8 py-4" data-id="element-282">
                Request a Quote
              </Link>
              <Link to="/services" className="btn-outline text-lg px-8 py-4 bg-brand-black/50 backdrop-blur-sm" data-id="element-283">
                Explore Services
              </Link>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 reveal" style={{
          transitionDelay: '200ms'
        }} data-id="element-284">
            {[{
            icon: CalendarIcon,
            text: 'Established in 2004'
          }, {
            icon: FlagIcon,
            text: '100% Citizen-Owned'
          }, {
            icon: AwardIcon,
            text: 'Professional AV Solutions'
          }, {
            icon: BriefcaseIcon,
            text: 'Corporate & Public Sector Ready'
          }].map((badge, i) => <div key={i} className="glass-card p-4 flex items-center gap-3" data-id="element-285">
                <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0" data-id="element-286">
                  <badge.icon className="w-5 h-5 text-brand-blue" data-id="element-287" />
                </div>
                <span className="text-sm font-medium text-white leading-tight" data-id="element-288">
                  {badge.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 2 - INTRO / WHO WE ARE */}
      <section className="py-20 md:py-32 px-6 bg-brand-charcoal relative" data-id="element-289">
        <div className="max-w-7xl mx-auto" data-id="element-290">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-291">
            <div className="reveal" data-id="element-292">
              <span className="text-brand-blue uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-293">
                Who We Are
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-294">
                Botswana's Trusted Audio-Visual &amp; Event Production Partner
              </h2>
              <p className="text-brand-muted-light text-lg leading-relaxed mb-6" data-id="element-295">
                AUDIOTECH is a 100% Botswana citizen-owned company established
                in 2004. Over the years, the company has built a strong
                reputation for delivering reliable sound, staging, lighting,
                visual equipment, and event production solutions across
                Botswana.
              </p>
              <p className="text-brand-muted-light text-lg leading-relaxed mb-8" data-id="element-296">
                We combine technical expertise, dependable service, and
                practical experience to support events, institutions, and
                organizations that need quality production and communication
                systems.
              </p>
              <Link to="/about" className="inline-flex items-center text-brand-blue font-medium hover:text-brand-cyan transition-colors group" data-id="element-297">
                Learn More About Us
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-298" />
              </Link>
            </div>
            <div className="reveal" style={{
            transitionDelay: '200ms'
          }} data-id="element-299">
              <div className="img-placeholder aspect-square md:aspect-[4/3] w-full shadow-glow-lg" data-id="element-300">
                <PlayIcon className="w-16 h-16 text-white/20" data-id="element-301" />
                <div className="absolute bottom-6 left-6 right-6 glass-card p-4 flex items-center gap-4" data-id="element-302">
                  <div className="w-12 h-12 rounded-full bg-brand-blue flex items-center justify-center shrink-0" data-id="element-303">
                    <AwardIcon className="w-6 h-6 text-white" data-id="element-304" />
                  </div>
                  <div data-id="element-305">
                    <div className="text-white font-bold text-lg" data-id="element-306">
                      20+ Years
                    </div>
                    <div className="text-brand-muted text-sm" data-id="element-307">
                      Industry Experience
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 - SERVICES OVERVIEW */}
      <section className="py-20 md:py-32 px-6 bg-brand-black relative" data-id="element-308">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-brand-blue/20 to-transparent" data-id="element-309" />
        <div className="max-w-7xl mx-auto" data-id="element-310">
          <div className="text-center mb-16 reveal" data-id="element-311">
            <span className="text-brand-blue uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-312">
              Our Services
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white" data-id="element-313">
              Comprehensive Audio-Visual &amp; Event Solutions
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-314">
            {[{
            icon: Volume2Icon,
            title: 'Sound / Public Address Systems',
            desc: 'Professional sound reinforcement and public address solutions for conferences, ceremonies, concerts, launches, and public events.'
          }, {
            icon: LayoutIcon,
            title: 'Stage Hire',
            desc: 'Reliable staging solutions for indoor and outdoor events, from compact platforms to larger event stage structures.'
          }, {
            icon: LightbulbIcon,
            title: 'Lighting Hire',
            desc: 'Dynamic lighting solutions that enhance visibility, atmosphere, and audience experience for all kinds of events.'
          }, {
            icon: MonitorIcon,
            title: 'Visual Equipment Hire',
            desc: 'Screens, projection, and visual presentation equipment that help your message reach your audience clearly and professionally.'
          }, {
            icon: VideoIcon,
            title: 'DVD & Video Production',
            desc: 'Video production support for events, presentations, recordings, and branded visual content.'
          }, {
            icon: CalendarIcon,
            title: 'Events & Promotions Management',
            desc: 'End-to-end support for event coordination, activation environments, branded experiences, and production delivery.'
          }].map((service, i) => <div key={i} className="glass-card p-8 group reveal" style={{
            transitionDelay: `${i * 100}ms`
          }} data-id="element-315">
                <div className="w-14 h-14 rounded-xl bg-brand-blue/10 flex items-center justify-center mb-6 group-hover:bg-brand-blue group-hover:text-white transition-colors text-brand-blue" data-id="element-316">
                  <service.icon className="w-7 h-7" data-id="element-317" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-318">
                  {service.title}
                </h3>
                <p className="text-brand-muted-light mb-6 leading-relaxed" data-id="element-319">
                  {service.desc}
                </p>
                <Link to="/services" className="inline-flex items-center text-sm font-medium text-brand-blue group-hover:text-brand-cyan transition-colors" data-id="element-320">
                  Learn More{' '}
                  <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-321" />
                </Link>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4 - WHY CHOOSE US */}
      <section className="py-20 md:py-32 px-6 bg-brand-charcoal relative overflow-hidden" data-id="element-322">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-blue/5 rounded-full blur-[120px] pointer-events-none" data-id="element-323" />
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-324">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-325">
            <div className="order-2 lg:order-1 reveal" data-id="element-326">
              <div className="grid grid-cols-2 gap-4" data-id="element-327">
                <div className="img-placeholder aspect-[3/4] w-full mt-8 shadow-glass" data-id="element-328">
                  <SettingsIcon className="w-12 h-12 text-white/20" data-id="element-329" />
                </div>
                <div className="img-placeholder aspect-[3/4] w-full shadow-glass" data-id="element-330">
                  <UsersIcon className="w-12 h-12 text-white/20" data-id="element-331" />
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2 reveal" style={{
            transitionDelay: '200ms'
          }} data-id="element-332">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-333">
                Technical Solutions That Perform Under Pressure
              </h2>
              <p className="text-brand-muted-light text-lg leading-relaxed mb-10" data-id="element-334">
                We understand that events and communication environments require
                precision, reliability, and professional execution. AUDIOTECH
                delivers practical, high-impact solutions backed by experience
                and attention to detail.
              </p>
              <div className="space-y-6" data-id="element-335">
                {[{
                icon: WrenchIcon,
                text: 'Professional-grade equipment'
              }, {
                icon: UsersIcon,
                text: 'Experienced technical support'
              }, {
                icon: ShieldCheckIcon,
                text: 'Dependable setup and execution'
              }, {
                icon: BuildingIcon,
                text: 'Suitable for corporate, public, and live event environments'
              }].map((feature, i) => <div key={i} className="flex items-start gap-4" data-id="element-336">
                    <div className="w-10 h-10 rounded-lg bg-brand-slate flex items-center justify-center shrink-0 border border-white/5" data-id="element-337">
                      <feature.icon className="w-5 h-5 text-brand-cyan" data-id="element-338" />
                    </div>
                    <div className="pt-2" data-id="element-339">
                      <h4 className="text-white font-medium" data-id="element-340">{feature.text}</h4>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 - RENTALS PREVIEW */}
      <section className="py-20 md:py-32 px-6 bg-brand-black" data-id="element-341">
        <div className="max-w-7xl mx-auto" data-id="element-342">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 reveal" data-id="element-343">
            <div data-id="element-344">
              <span className="text-brand-blue uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-345">
                Equipment &amp; Structure Rentals
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white" data-id="element-346">
                Premium Rental Solutions for Every Event Scale
              </h2>
            </div>
            <Link to="/rentals" className="btn-glow shrink-0" data-id="element-347">
              Request Rental Pricing
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-348">
            {['Stage Roofing with Shading', 'Scaffolding Stage', 'Beauty Pageant / Parade Ramp', 'Aluminum Stage Platforms', 'Sound Systems', 'Lighting Systems', 'Visual Display Equipment'].map((item, i) => <div key={i} className="glass-card p-6 flex items-center justify-center text-center reveal" style={{
            transitionDelay: `${i * 50}ms`
          }} data-id="element-349">
                <span className="text-white font-medium" data-id="element-350">{item}</span>
              </div>)}
          </div>
          <p className="text-brand-muted text-center italic reveal" data-id="element-351">
            * Rental options can be tailored to suit your event scale and
            technical requirements.
          </p>
        </div>
      </section>

      {/* SECTION 6 - INDUSTRIES */}
      <section className="py-20 md:py-32 px-6 bg-brand-charcoal border-y border-white/5" data-id="element-352">
        <div className="max-w-7xl mx-auto text-center" data-id="element-353">
          <div className="reveal mb-16" data-id="element-354">
            <span className="text-brand-blue uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-355">
              Industries We Serve
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white" data-id="element-356">
              Trusted Across Sectors
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-357">
            {['Corporate Events', 'Government & Public Sector', 'Conferences & Workshops', 'Churches & Worship Events', 'Schools & Institutions', 'Product Launches & Promotions', 'Entertainment Events', 'Community Events'].map((industry, i) => <div key={i} className="glass-card p-6 md:p-8 flex flex-col items-center justify-center text-center group reveal" style={{
            transitionDelay: `${i * 50}ms`
          }} data-id="element-358">
                <BuildingIcon className="w-8 h-8 text-brand-muted mb-4 group-hover:text-brand-blue transition-colors" data-id="element-359" />
                <span className="text-white font-medium text-sm md:text-base" data-id="element-360">
                  {industry}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 7 - GALLERY PREVIEW */}
      <section className="py-20 md:py-32 px-6 bg-brand-black" data-id="element-361">
        <div className="max-w-7xl mx-auto" data-id="element-362">
          <div className="text-center mb-16 reveal" data-id="element-363">
            <span className="text-brand-blue uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-364">
              Our Work
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-365">
              Events &amp; Installations We're Proud Of
            </h2>
            <p className="text-brand-muted-light text-lg max-w-2xl mx-auto" data-id="element-366">
              Explore selected projects, event setups, and technical
              environments delivered by AUDIOTECH.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-id="element-367">
            {[1, 2, 3, 4, 5, 6].map((item, i) => <div key={i} className={`img-placeholder group reveal ${i === 0 || i === 3 ? 'md:col-span-2 aspect-[2/1]' : 'aspect-square'}`} style={{
            transitionDelay: `${i * 100}ms`
          }} data-id="element-368">
                <div className="absolute inset-0 bg-brand-blue/20 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-369" />
                <CameraIcon className="w-12 h-12 text-white/20" data-id="element-370" />
                <div className="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-brand-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-20 translate-y-4 group-hover:translate-y-0 duration-300" data-id="element-371">
                  <h4 className="text-white font-bold text-lg" data-id="element-372">
                    Project Name {item}
                  </h4>
                  <p className="text-brand-cyan text-sm" data-id="element-373">Event Production</p>
                </div>
              </div>)}
          </div>

          <div className="text-center reveal" data-id="element-374">
            <Link to="/gallery" className="btn-outline" data-id="element-375">
              View Full Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 8 - PROCESS */}
      <section className="py-20 md:py-32 px-6 bg-brand-charcoal" data-id="element-376">
        <div className="max-w-7xl mx-auto" data-id="element-377">
          <div className="text-center mb-20 reveal" data-id="element-378">
            <span className="text-brand-blue uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-379">
              How We Work
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-380">
              From Concept to Completion
            </h2>
            <p className="text-brand-muted-light text-lg max-w-2xl mx-auto" data-id="element-381">
              We make it easy to move from idea to execution with a structured
              and dependable delivery process.
            </p>
          </div>

          <div className="relative" data-id="element-382">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-brand-slate" data-id="element-383" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10" data-id="element-384">
              {[{
              icon: MessageSquareIcon,
              title: 'Consultation',
              desc: 'Understanding your technical requirements and event goals.'
            }, {
              icon: ClipboardListIcon,
              title: 'Planning & Quotation',
              desc: 'Detailed technical design and transparent pricing.'
            }, {
              icon: SettingsIcon,
              title: 'Setup & Delivery',
              desc: 'Professional installation and live event execution.'
            }, {
              icon: CheckCircleIcon,
              title: 'Support & Closeout',
              desc: 'Ongoing support and efficient post-event teardown.'
            }].map((step, i) => <div key={i} className="text-center reveal" style={{
              transitionDelay: `${i * 150}ms`
            }} data-id="element-385">
                  <div className="w-24 h-24 mx-auto bg-brand-black rounded-full border-4 border-brand-charcoal flex items-center justify-center mb-6 shadow-glow relative group" data-id="element-386">
                    <div className="absolute inset-0 rounded-full bg-brand-blue/20 scale-0 group-hover:scale-100 transition-transform duration-300" data-id="element-387" />
                    <step.icon className="w-10 h-10 text-brand-blue relative z-10" data-id="element-388" />
                    <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-brand-cyan text-brand-black font-bold flex items-center justify-center text-sm" data-id="element-389">
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-390">
                    {step.title}
                  </h3>
                  <p className="text-brand-muted text-sm leading-relaxed" data-id="element-391">
                    {step.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 - TESTIMONIALS */}
      <section className="py-20 md:py-32 px-6 bg-brand-black relative overflow-hidden" data-id="element-392">
        <div className="absolute left-0 top-0 w-[600px] h-[600px] bg-brand-cyan/5 rounded-full blur-[100px] pointer-events-none" data-id="element-393" />
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-394">
          <div className="text-center mb-16 reveal" data-id="element-395">
            <span className="text-brand-blue uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-396">
              Client Feedback
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white" data-id="element-397">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-398">
            {[{
            quote: "AUDIOTECH delivered exceptional sound and lighting for our annual corporate gala. Their team was professional, punctual, and the quality of equipment was outstanding. We've worked with them on multiple events since.",
            name: 'Thabo M.',
            role: 'Corporate Events Manager'
          }, {
            quote: 'We needed a complete AV setup for a government conference with over 500 delegates. AUDIOTECH handled everything from staging to visual displays. Reliable and thoroughly professional.',
            name: 'Kelebogile S.',
            role: 'Public Sector Coordinator'
          }, {
            quote: "From our church's weekly services to special events, AUDIOTECH has been our trusted partner for sound systems and technical support. They understand our needs and always deliver.",
            name: 'Pastor David N.',
            role: 'Faith Community Church'
          }].map((testimonial, i) => <div key={i} className="glass-card p-8 relative reveal" style={{
            transitionDelay: `${i * 150}ms`
          }} data-id="element-399">
                <QuoteIcon className="absolute top-6 right-6 w-12 h-12 text-white/5" data-id="element-400" />
                <div className="flex gap-1 mb-6" data-id="element-401">
                  {[1, 2, 3, 4, 5].map(star => <svg key={star} className="w-5 h-5 text-brand-blue" fill="currentColor" viewBox="0 0 20 20" data-id="element-402">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-403" />
                    </svg>)}
                </div>
                <p className="text-brand-muted-light italic mb-8 relative z-10 leading-relaxed" data-id="element-404">
                  "{testimonial.quote}"
                </p>
                <div data-id="element-405">
                  <h4 className="text-white font-bold" data-id="element-406">{testimonial.name}</h4>
                  <p className="text-brand-cyan text-sm" data-id="element-407">{testimonial.role}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 10 - FINAL CTA */}
      <section className="py-24 px-6 relative overflow-hidden" data-id="element-408">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-charcoal to-brand-black z-0" data-id="element-409" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-brand-blue/20 via-transparent to-transparent z-0" data-id="element-410" />

        <div className="max-w-4xl mx-auto text-center relative z-10 glass-card p-12 md:p-20 border-brand-blue/20 reveal" data-id="element-411">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-412">
            Need Professional Event or AV Support?
          </h2>
          <p className="text-xl text-brand-muted-light mb-10 max-w-2xl mx-auto" data-id="element-413">
            Let's help you plan the right sound, stage, lighting, and visual
            solution for your next project.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-414">
            <Link to="/contact" className="btn-glow text-lg px-8 py-4" data-id="element-415">
              Request a Quote
            </Link>
            <Link to="/contact" className="btn-outline text-lg px-8 py-4 bg-brand-black/50" data-id="element-416">
              Contact AUDIOTECH
            </Link>
          </div>
        </div>
      </section>
    </main>;
}
// Helper icon for gallery
function CameraIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-417">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" data-id="element-418" />
      <circle cx="12" cy="13" r="3" data-id="element-419" />
    </svg>;
}